import { useMutation, useQueryClient } from '@tanstack/react-query';
import LeadLegalInfoService, {
  IUpsertLeadLegalInfoParams,
} from '@clickersphere/core/services/LeadLegalInfoService';
import { useBuildQueryKey } from '../useBuildQueryKey';
import {
  LEAD_LEGAL_INFO_QUERY_KEY,
  LeadLegalInfoQueryData,
} from './useLeadLegalInfo';

export function useUpsertLeadLegalInfo() {
  const queryClient = useQueryClient();
  const { buildQueryKey } = useBuildQueryKey();

  const {
    isPending: upsertLeadLegalInfoIsPending,
    mutateAsync: upsertLeadLegalInfoAsync,
  } = useMutation({
    mutationFn: async ({
      leadId,
      data,
    }: {
      leadId: string;
      data: IUpsertLeadLegalInfoParams;
    }) => {
      const legalInfo = await LeadLegalInfoService.upsert(leadId, data);
      return legalInfo as LeadLegalInfoQueryData;
    },
    onMutate: async (variables) => {
      await queryClient.cancelQueries({
        queryKey: buildQueryKey([LEAD_LEGAL_INFO_QUERY_KEY, variables.leadId]),
      });

      const oldLeadLegalInfo =
        queryClient.getQueryData<LeadLegalInfoQueryData | null>(
          buildQueryKey([LEAD_LEGAL_INFO_QUERY_KEY, variables.leadId]),
        );

      queryClient.setQueryData<LeadLegalInfoQueryData | null>(
        buildQueryKey([LEAD_LEGAL_INFO_QUERY_KEY, variables.leadId]),
        (old) => ({
          id: old?.id ?? String(crypto.randomUUID()),
          leadId: variables.leadId,
          tenantId: old?.tenantId ?? '',
          createdAt: old?.createdAt ?? new Date().toISOString(),
          cpf: variables.data.cpf,
          rg: variables.data.rg,
          profession: variables.data.profession,
          maritalStatus: variables.data.maritalStatus,
          nationality: variables.data.nationality,
          address: variables.data.address ?? null,
          queryStatus: 'pending',
        }),
      );

      return { oldLeadLegalInfo };
    },
    onSuccess: async (data, variables) => {
      await queryClient.cancelQueries({
        queryKey: buildQueryKey([LEAD_LEGAL_INFO_QUERY_KEY, variables.leadId]),
      });

      queryClient.setQueryData<LeadLegalInfoQueryData | null>(
        buildQueryKey([LEAD_LEGAL_INFO_QUERY_KEY, variables.leadId]),
        data,
      );
    },
    onError: async (_error, variables, context) => {
      await queryClient.cancelQueries({
        queryKey: buildQueryKey([LEAD_LEGAL_INFO_QUERY_KEY, variables.leadId]),
      });

      queryClient.setQueryData(
        buildQueryKey([LEAD_LEGAL_INFO_QUERY_KEY, variables.leadId]),
        context?.oldLeadLegalInfo ?? null,
      );
    },
  });

  return {
    upsertLeadLegalInfoAsync,
    upsertLeadLegalInfoIsPending,
  };
}
